import React, { useState } from "react";
import { X, FileText, Eye, Download, Loader2, CheckCircle, XCircle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin-side document viewer for a single manager (Aadhaar / PAN / Photo).
 * Extracted from TeamHubTab.js for readability — pure presentational,
 * prop-driven: auth/state lives in the parent.
 */
const DocViewerModal = ({ isOpen, onClose, manager, authAxios }) => {
  const [viewing, setViewing] = useState(null);
  const [imgUrl, setImgUrl] = useState("");
  const [loadingDoc, setLoadingDoc] = useState(false);

  const viewDoc = async (docType) => {
    setLoadingDoc(true);
    setViewing(docType);
    try {
      const res = await authAxios.get(
        `${API_URL}/api/admin/managers/${manager.username}/documents/${docType}`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      setImgUrl(url);
    } catch {
      setImgUrl("");
    }
    setLoadingDoc(false);
  };

  const downloadDoc = async (docType) => {
    try {
      const res = await authAxios.get(
        `${API_URL}/api/admin/managers/${manager.username}/documents/${docType}`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${manager.username}_${docType}.${manager.documents?.[docType]?.filename?.split('.').pop() || 'jpg'}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert("Failed to download document");
    }
  };

  if (!isOpen) return null;
  const docs = manager.documents || {};

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[85vh] overflow-y-auto" data-testid="doc-viewer-modal">
        <div className="p-6 border-b flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-[#1E1B4B]">{manager.name}'s Documents</h3>
            <p className="text-sm text-[#94a3b8]">@{manager.username}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          {["aadhaar", "pan", "photo"].map((docType) => {
            const doc = docs[docType];
            const uploaded = !!doc;
            return (
              <div key={docType} className={`p-4 rounded-xl border ${uploaded ? "border-green-200 bg-green-50" : "border-gray-200 bg-gray-50"}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText size={20} className={uploaded ? "text-green-600" : "text-gray-400"} />
                    <div>
                      <p className="font-medium text-sm text-[#1E1B4B] capitalize">{docType === "aadhaar" ? "Aadhaar Card" : docType === "pan" ? "PAN Card" : "Photo ID"}</p>
                      {uploaded && <p className="text-xs text-[#94a3b8]">{doc.filename} - {new Date(doc.uploaded_at).toLocaleDateString()}</p>}
                      {!uploaded && <p className="text-xs text-red-400">Not uploaded</p>}
                    </div>
                  </div>
                  {uploaded && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => viewDoc(docType)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-200"
                        data-testid={`view-doc-${docType}`}
                      >
                        <Eye size={14} /> View
                      </button>
                      <button
                        onClick={() => downloadDoc(docType)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200"
                        data-testid={`download-doc-${docType}`}
                      >
                        <Download size={14} /> Download
                      </button>
                    </div>
                  )}
                </div>
                {viewing === docType && (
                  <div className="mt-3">
                    {loadingDoc ? (
                      <div className="flex justify-center py-4"><Loader2 className="animate-spin text-blue-500" size={24} /></div>
                    ) : imgUrl ? (
                      <img src={imgUrl} alt={docType} className="max-h-64 rounded-lg mx-auto border" />
                    ) : (
                      <p className="text-sm text-red-500 text-center py-2">Failed to load document</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Rules & NDA Status */}
          <div className="p-4 rounded-xl border border-gray-200">
            <p className="font-medium text-sm text-[#1E1B4B] mb-2">Agreements</p>
            <div className="flex gap-4">
              <div className="flex items-center gap-2 text-sm">
                {manager.rules_accepted ? <CheckCircle size={16} className="text-green-500" /> : <XCircle size={16} className="text-red-400" />}
                <span>Rules & Regulations</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                {manager.nda_accepted ? <CheckCircle size={16} className="text-green-500" /> : <XCircle size={16} className="text-red-400" />}
                <span>NDA</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocViewerModal;
